/**
 * Community — landlord-only portal page where verified landlords can:
 *  - Post questions / vendor recs / regulation discussions / troubleshooting
 *  - Comment on others' posts
 *  - Delete their own content
 *  - Block other landlords (bidirectional hide)
 *  - Browse a province-filtered vendor directory
 *  - Edit their community profile (avatar, bio, doors, province)
 *
 * Route: /community  (auth required, landlord role)
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2, MessageCircle, UserCircle2, Trash2, ShieldOff, Send, Tag, MapPin, Image as ImageIcon, Search, Wrench, Phone, Globe, Star, Rocket, CheckCircle2, Video, Calendar, Camera, X, Trophy, Award, Box } from "lucide-react";
import { toast } from "sonner";
import PhotoPicker from "@/components/PhotoPicker";
import MatterportViewer from "@/components/MatterportViewer";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const POST_CATS_LANDLORD = [
  ["general", "General"],
  ["vendor-rec", "Vendor recs"],
  ["regulations", "Regulations"],
  ["troubleshooting", "Troubleshooting"],
  ["lease-questions", "Lease questions"],
  ["tax-questions", "Tax (T776/NR4)"],
  ["marketplace", "Marketplace"],
];
const POST_CATS_TENANT = [
  ["general", "General"],
  ["tenant-rights", "Tenant rights"],
  ["roommate-search", "Roommate search"],
  ["neighbourhood", "Neighbourhood"],
  ["move-in-tips", "Move-in tips"],
  ["rental-search-help", "Rental search help"],
  ["marketplace", "Marketplace"],
];
const POST_CATS = POST_CATS_LANDLORD; // legacy reference; FeedPanel picks based on scope
const PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function Community({ scope = "landlord" }) {
  const { user } = useAuth();
  const userId = user?.user_id || user?.id || user?._id;
  // Read ?tab=… from the URL so we can deep-link tenants straight to the
  // rental listings from the Dashboard "Browse rentals" CTA + sidebar.
  const initialTab = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get("tab");
      return q && ["feed", "vendors", "listings", "profile"].includes(q) ? q : "feed";
    } catch { return "feed"; }
  })();
  const [tab, setTab] = useState(initialTab);

  if (!userId) {
    return <div className="min-h-screen flex items-center justify-center text-slate-600">Login required.</div>;
  }

  const isTenant = scope === "tenant";
  const title = isTenant ? "Tenant Community" : "Landlord Community";
  const subtitle = isTenant
    ? "Meet your neighbours, swap apartment-hunting tips, find roommates, and get answers to the questions your landlord can't."
    : "Talk shop with other Canadian landlords — share vendor recommendations, work through tricky situations together, and stay on top of provincial law changes.";
  const tabs = isTenant
    ? [["feed","Feed"],["listings","Rentals"],["profile","My bio"]]
    : [["feed","Feed"],["vendors","Vendors"],["listings","Listings"],["profile","My profile"]];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
          <nav className="mt-5 flex flex-wrap gap-2">
            {tabs.map(([k,l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`community-tab-${k}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`}>
                {l}
              </button>
            ))}
          </nav>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-6">
        <BadgeRedirectHandler userId={userId}/>
        {tab === "feed" && <FeedPanel userId={userId} scope={scope}/>}
        {tab === "vendors" && !isTenant && <VendorsPanel/>}
        {tab === "listings" && <ListingsPanel userId={userId} isTenant={isTenant}/>}
        {tab === "profile" && <ProfilePanel userId={userId} scope={scope} onTabChange={setTab}/>}
      </main>
    </div>
  );
}

// Polls /badge/status/{sid} after a Stripe success redirect (URL has
// ?paid=badge&session_id=…) and clears the query string when done.
function BadgeRedirectHandler({ userId }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("paid") !== "badge") return;
    const sid = sp.get("session_id");
    if (!sid) return;
    (async () => {
      try {
        await fetch(`${API}/api/monetization/badge/status/${sid}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
        setDone(true);
        // Strip the query so a refresh doesn't re-trigger.
        const url = new URL(window.location.href);
        url.searchParams.delete("paid"); url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
      } catch { /* swallow */ }
    })();
  }, [userId]);
  if (!done) return null;
  return (
    <div className="mb-4 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 inline-flex items-center gap-2" data-testid="badge-purchase-success">
      <CheckCircle2 className="w-5 h-5"/> Top Contributor badge activated — it shows on all your posts for 90 days.
    </div>
  );
}

// ─────────────────────────── FEED ──────────────────────────
function FeedPanel({ userId, scope = "landlord" }) {
  const cats = scope === "tenant" ? POST_CATS_TENANT : POST_CATS_LANDLORD;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [province, setProvince] = useState("");
  const [body, setBody] = useState("");
  const [postCat, setPostCat] = useState("general");
  const [postImg, setPostImg] = useState("");
  const [postProv, setPostProv] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const qs = new URLSearchParams({ scope });
      if (category) qs.append("category", category);
      if (province) qs.append("province", province);
      const r = await fetch(`${API}/api/community/posts?${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "load failed");
      setPosts(d.items || []);
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, [userId, category, province, scope]);
  useEffect(() => { load(); }, [load]);

  async function submitPost(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/community/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ body, category: postCat, image_url: postImg || null, province: postProv || null, scope }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "post failed");
      setBody(""); setPostImg(""); setPostProv("");
      await load();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <form onSubmit={submitPost} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="community-compose">
          <div className="font-display text-base font-extrabold text-slate-900">Start a discussion</div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} required placeholder="Ask a question or share something useful…"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md outline-none focus:border-emerald-500" data-testid="community-post-body"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={postCat} onChange={(e) => setPostCat(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded" data-testid="community-post-category">
              {cats.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select value={postProv} onChange={(e) => setPostProv(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded">
              <option value="">All provinces</option>
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <PhotoPicker
            value={postImg}
            onChange={(url) => setPostImg(url || "")}
            kind="community"
            apiBase={`${API}/api`}
            testid="community-post-photo"
            label="Add a photo"
          />
          <button type="submit" disabled={busy} data-testid="community-post-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>} Post
          </button>
        </form>
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Filter</div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded">
            <option value="">All categories</option>
            {cats.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={province} onChange={(e) => setProvince(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded">
            <option value="">All provinces</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div className="lg:col-span-2 space-y-4">
        {error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">{error}</div>}
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 inline"/></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl">No posts yet — start the conversation.</div>
        ) : (
          posts.map(p => <PostCard key={p.id} post={p} userId={userId} onChange={load}/>)
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── LISTINGS (tenant feed) ──────────────────────────
function ListingsPanel({ userId }) {
  const [items, setItems] = useState([]);
  const [province, setProvince] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [rentMax, setRentMax] = useState("");
  const [petsOnly, setPetsOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [liveTourFor, setLiveTourFor] = useState(null);
  const [bioFor, setBioFor] = useState(null);
  const [tourFor, setTourFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (province) qs.append("province", province);
    if (bedrooms) qs.append("bedrooms_min", bedrooms);
    if (rentMax) qs.append("rent_max", rentMax);
    if (petsOnly) qs.append("pets_allowed", "1");
    const r = await fetch(`${API}/api/listings?${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
    const d = await r.json();
    setItems(d.items || []);
    setLoading(false);
  }, [userId, province, bedrooms, rentMax, petsOnly]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="listings-panel">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-900">
        <b>Available rentals across Canada.</b> Tap a province below to narrow to your search area, or browse all of them.
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="province-chips">
        <button onClick={() => setProvince("")} className={`px-3 py-1 rounded-full text-xs font-bold border ${province === "" ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-700 hover:border-emerald-400"}`} data-testid="province-chip-all">All provinces</button>
        {PROVINCES.map(p => (
          <button key={p} onClick={() => setProvince(p === province ? "" : p)} className={`px-3 py-1 rounded-full text-xs font-bold border ${province === p ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-700 hover:border-emerald-400"}`} data-testid={`province-chip-${p}`}>{p}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded">
          <option value="">Any beds</option>
          {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}+ beds</option>)}
        </select>
        <input value={rentMax} onChange={(e) => setRentMax(e.target.value)} placeholder="Max rent" type="number" className="px-2 py-1.5 text-xs border border-slate-300 rounded w-28"/>
        <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={petsOnly} onChange={(e) => setPetsOnly(e.target.checked)}/> Pets OK</label>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-600"/> : items.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl">No listings match your filters yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(l => (
            <div key={l.id} className={`rounded-xl border overflow-hidden ${l.boost_active ? "border-emerald-300 ring-2 ring-emerald-200" : "border-slate-200"} bg-white`} data-testid={`listing-${l.id}`}>
              {l.image_urls?.[0] && <img src={l.image_urls[0]} alt={l.title} className="w-full h-44 object-cover"/>}
              <div className="p-4">
                <div className="flex items-baseline justify-between">
                  <div className="font-bold text-slate-900 truncate flex items-center gap-2">
                    {l.boost_active && <Star className="w-3.5 h-3.5 text-emerald-600 fill-current shrink-0"/>}
                    {l.title}
                  </div>
                  <div className="font-display text-lg font-extrabold text-emerald-700 tabular-nums shrink-0 ml-2">${l.rent_cad}/mo</div>
                </div>
                <div className="text-xs text-slate-500 mt-1">{l.city}, {l.province} · {l.bedrooms} bed · {l.bathrooms} bath {l.pets_allowed && "· pets OK"}{l.furnished && " · furnished"}</div>
                <p className="text-xs text-slate-600 mt-2 line-clamp-2">{l.description}</p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {l.tour_video_url && (
                    <a href={l.tour_video_url} target="_blank" rel="noreferrer" data-testid={`watch-tour-${l.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800">
                      <Video className="w-3.5 h-3.5"/> Watch walkthrough
                    </a>
                  )}
                  {l.matterport_model_id && (
                    <button onClick={() => setTourFor(l)} data-testid={`view-3d-tour-${l.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800">
                      <Box className="w-3.5 h-3.5"/> View 3D tour
                    </button>
                  )}
                  <button onClick={() => setLiveTourFor(l)} data-testid={`request-tour-${l.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900">
                    <Calendar className="w-3.5 h-3.5"/> Request live tour
                  </button>
                  {l.landlord_id && (
                    <button onClick={() => setBioFor(l.landlord_id)} data-testid={`view-landlord-bio-${l.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-900">
                      View landlord bio →
                    </button>
                  )}
                  {l.contact_email && <a href={`mailto:${l.contact_email}`} className="ml-auto text-xs text-emerald-700 font-bold hover:underline">Contact landlord →</a>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {liveTourFor && <LiveTourRequestModal listing={liveTourFor} onClose={() => setLiveTourFor(null)}/>}
      {bioFor && <ProfileModal userId={bioFor} onClose={() => setBioFor(null)}/>}
      {tourFor && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setTourFor(null)} data-testid="matterport-modal-overlay">
          <div className="bg-white rounded-2xl w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-slate-200">
              <div className="font-display font-bold text-slate-900 flex items-center gap-2"><Box className="w-4 h-4 text-emerald-700"/> {tourFor.title}</div>
              <button onClick={() => setTourFor(null)} className="p-1.5 rounded hover:bg-slate-100" data-testid="matterport-modal-close"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-3"><MatterportViewer listingId={tourFor.id} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── LIVE TOUR REQUEST MODAL ──────────────────────────
function LiveTourRequestModal({ listing, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_slots: "", note: "", self_tour: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const slots = form.preferred_slots.split(",").map(s => s.trim()).filter(Boolean).slice(0, 5);
      const r = await fetch(`${API}/api/tours/listing/${listing.id}/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone || null,
          preferred_slots: slots, note: form.note || null, self_tour: form.self_tour,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not send request");
      setDone(true);
    } catch (err) { setError(String(err.message)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="live-tour-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-lg font-bold text-slate-900 mb-1">Request a tour</div>
        <div className="text-xs text-slate-500 mb-4">{listing.title} · {listing.city}, {listing.province}</div>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3"/>
            <div className="font-bold text-slate-900">Request sent</div>
            <div className="text-xs text-slate-600 mt-1">The landlord will confirm your slot and send a meeting link.</div>
            <button onClick={onClose} className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="tour-name"/>
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="tour-email"/>
            <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm"/>
            <input placeholder="Preferred slots (comma-separated)" value={form.preferred_slots} onChange={(e) => setForm({...form, preferred_slots: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="tour-slots"/>
            <textarea placeholder="Anything we should know?" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" rows={2}/>
            <label className="flex items-start gap-2 text-xs text-slate-700"><input type="checkbox" checked={form.self_tour} onChange={(e) => setForm({...form, self_tour: e.target.checked})} className="mt-0.5" data-testid="tour-self"/> Prefer an unaccompanied self-tour (smart-lock PIN if the landlord enables it).</label>
            {error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1.5 rounded">{error}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:text-slate-900">Cancel</button>
              <button type="submit" disabled={busy} className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50" data-testid="tour-submit">{busy ? "Sending…" : "Send request"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, userId, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadDetail() {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/community/posts/${post.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
      const d = await r.json();
      if (r.ok) setDetail(d);
    } finally { setBusy(false); }
  }
  async function toggle() { if (!expanded) await loadDetail(); setExpanded(!expanded); }

  async function deletePost() {
    if (!window.confirm("Delete this post?")) return;
    await fetch(`${API}/api/community/posts/${post.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
    onChange?.();
  }

  async function blockAuthor() {
    if (!window.confirm(`Block ${post.author?.display_name}? They won't be able to see your posts or comments, and you won't see theirs.`)) return;
    await fetch(`${API}/api/community/block`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
      body: JSON.stringify({ target_user_id: post.author_id }),
    });
    onChange?.();
  }

  async function postComment(e) {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await fetch(`${API}/api/community/posts/${post.id}/comments`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ body: comment }),
      });
      setComment("");
      await loadDetail();
      onChange?.();
    } finally { setBusy(false); }
  }

  async function deleteComment(cid) {
    if (!window.confirm("Delete comment?")) return;
    await fetch(`${API}/api/community/comments/${cid}`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
    await loadDetail();
    onChange?.();
  }

  const a = post.author || {};
  const [profileOpen, setProfileOpen] = useState(false);
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`community-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => setProfileOpen(true)} data-testid={`open-profile-${a.user_id || post.author_id}`} className="shrink-0 rounded-full hover:ring-2 hover:ring-emerald-200 transition" title={`View ${a.display_name || "member"}'s profile`}>
          {a.avatar_url ? <img src={a.avatar_url.startsWith("http") ? a.avatar_url : `${API}${a.avatar_url}`} alt={a.display_name || "Member"} className="w-10 h-10 rounded-full object-cover bg-slate-100"/> : <UserCircle2 className="w-10 h-10 text-slate-300"/>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <button onClick={() => setProfileOpen(true)} className="font-bold text-slate-900 hover:text-emerald-700 hover:underline" data-testid={`open-profile-name-${a.user_id || post.author_id}`}>{a.display_name || "Member"}</button>
            {a.top_contributor && (
              <span data-testid={`top-contributor-chip-${post.id}`} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 font-bold shadow-sm">
                <Star className="w-2.5 h-2.5 fill-current"/> Top Contributor
              </span>
            )}
            {a.province && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">{a.province}</span>}
            {a.door_count != null && <span className="text-[10px] text-slate-500">{a.door_count} doors</span>}
            <span className="text-[10px] text-slate-400">· {fmt(post.created_at)}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 inline-flex items-center gap-0.5"><Tag className="w-2.5 h-2.5"/>{post.category.replace('-', ' ')}</span>
            {post.province && <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5"/>{post.province}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {post.can_delete && (
            <button onClick={deletePost} title="Delete" className="text-rose-600 hover:bg-rose-50 p-1.5 rounded" data-testid={`delete-post-${post.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
          )}
          {!post.can_delete && (
            <button onClick={blockAuthor} title="Block landlord" className="text-slate-400 hover:bg-slate-100 hover:text-rose-600 p-1.5 rounded" data-testid={`block-author-${post.id}`}><ShieldOff className="w-3.5 h-3.5"/></button>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-wrap">{post.body}</p>
      {post.image_url && <img src={post.image_url} alt="" className="rounded-lg max-h-80 border border-slate-100"/>}
      <button onClick={toggle} className="text-xs text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1" data-testid={`toggle-comments-${post.id}`}>
        <MessageCircle className="w-3.5 h-3.5"/> {post.comment_count} comment{post.comment_count === 1 ? "" : "s"}
      </button>
      {expanded && (
        <div className="border-t border-slate-100 pt-3 space-y-3">
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400"/>}
          {(detail?.comments || []).map(c => {
            const ca = c.author || {};
            return (
              <div key={c.id} className="flex items-start gap-2">
                <UserCircle2 className="w-6 h-6 text-slate-300 shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="text-xs"><span className="font-bold text-slate-800">{ca.display_name}</span> <span className="text-slate-400">· {fmt(c.created_at)}</span></div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.body}</div>
                </div>
                {c.can_delete && <button onClick={() => deleteComment(c.id)} className="text-rose-600 hover:bg-rose-50 p-1 rounded" title="Delete"><Trash2 className="w-3 h-3"/></button>}
              </div>
            );
          })}
          <form onSubmit={postComment} className="flex gap-2">
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" className="flex-1 px-3 py-1.5 text-xs border border-slate-300 rounded outline-none focus:border-emerald-500" data-testid={`comment-input-${post.id}`}/>
            <button type="submit" disabled={busy || !comment.trim()} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50" data-testid={`comment-submit-${post.id}`}>Send</button>
          </form>
        </div>
      )}
      {profileOpen && <ProfileModal userId={a.user_id || post.author_id} onClose={() => setProfileOpen(false)}/>}
    </article>
  );
}

// ─────────────────────────── PROFILE MODAL ──────────────────────────
function ProfileModal({ userId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch(`${API}/api/community/profile/${userId}/detail`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setDetail)
      .catch(() => setError("Could not load profile"));
  }, [userId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="profile-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <div className="font-display text-lg font-extrabold text-slate-900">Profile</div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="profile-modal-close"><X className="w-4 h-4"/></button>
        </div>
        {!detail && !error && <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto"/></div>}
        {error && <div className="p-6 text-center text-sm text-rose-600">{error}</div>}
        {detail && (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-4">
              {detail.avatar_url ? (
                <img src={detail.avatar_url.startsWith("http") ? detail.avatar_url : `${API}${detail.avatar_url}`} alt={detail.display_name} className="w-16 h-16 rounded-full object-cover bg-slate-100"/>
              ) : (
                <UserCircle2 className="w-16 h-16 text-slate-300"/>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display text-xl font-extrabold text-slate-900" data-testid="profile-modal-name">{detail.display_name || "Member"}</div>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {detail.role && <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">{detail.role}</span>}
                  {detail.province && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">{detail.province}</span>}
                  {detail.door_count != null && <span className="text-[10px] text-slate-500">{detail.door_count} doors</span>}
                  {detail.top_contributor && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-amber-950 font-bold"><Star className="w-2.5 h-2.5 fill-current"/> Top Contributor</span>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center"><div className="font-display text-2xl font-extrabold text-emerald-900" data-testid="profile-modal-points">{detail.points || 0}</div><div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Points</div></div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center"><div className="font-display text-2xl font-extrabold text-slate-900">{detail.post_count || 0}</div><div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Posts</div></div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center"><div className="font-display text-2xl font-extrabold text-slate-900">{detail.comment_count || 0}</div><div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Comments</div></div>
            </div>
            {detail.bio && (
              <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Bio</div><p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.bio}</p></div>
            )}
            {detail.about_me && (
              <div><div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">About</div><p className="text-sm text-slate-700 whitespace-pre-wrap">{detail.about_me}</p></div>
            )}
            {(detail.recent_posts?.length || 0) > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Recent posts</div>
                <div className="space-y-2">
                  {detail.recent_posts.map(p => (
                    <div key={p.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`profile-recent-post-${p.id}`}>
                      <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 mb-1">{p.category?.replace('-', ' ') || "general"}</div>
                      <p className="text-sm text-slate-700 line-clamp-2">{p.body}</p>
                      <div className="text-[10px] text-slate-400 mt-1">{new Date(p.created_at).toLocaleDateString("en-CA")} · {p.comment_count || 0} comments</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── VENDORS ──────────────────────────
function VendorsPanel() {
  const [vendors, setVendors] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ categories: [], provinces: [] });
  const [province, setProvince] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (province) qs.append("province", province);
    if (category) qs.append("category", category);
    if (q) qs.append("q", q);
    const [r, t] = await Promise.all([
      fetch(`${API}/api/vendors?${qs}`),
      fetch(`${API}/api/vendors/categories`),
    ]);
    const rd = await r.json(); setVendors(rd.items || []);
    const td = await t.json(); setTaxonomy(td);
    setLoading(false);
  }, [province, category, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
        <div className="font-display text-base font-extrabold text-emerald-900 mb-1">Vendor directory</div>
        <p className="text-xs text-emerald-800">A curated list of <b>plumbers, electricians, locksmiths, CPAs, and trades</b> our Canadian landlords actually use. Filter by your province and trade — every vendor has been verified for licensing and insurance.</p>
        <p className="text-xs text-emerald-800 mt-1.5">Run a service business? <Link to="/vendor-apply" data-testid="vendors-apply-link" className="underline font-bold">Apply to be listed</Link> — free basic profile, or <b>$49/mo Featured</b> for a pinned spot at the top of your trade + province.</p>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={province} onChange={(e) => setProvince(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded" data-testid="vendors-province">
          <option value="">All provinces</option>
          {taxonomy.provinces?.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded" data-testid="vendors-category">
          <option value="">All trades</option>
          {taxonomy.categories?.map(c => <option key={c} value={c}>{c.replace('-',' ')}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-300 rounded outline-none focus:border-emerald-500"/>
        </div>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto block my-10"/>
      ) : vendors.length === 0 ? (
        <div className="text-center py-10 px-6 border border-dashed border-slate-200 rounded-xl">
          <Wrench className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
          <div className="font-display text-base font-bold text-slate-700">No vendors match those filters</div>
          <p className="text-xs text-slate-500 mt-1">Try widening your province or trade selection — or drop a request in the Feed and another landlord will recommend someone they trust.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vendors-list">
          {vendors.map(v => (
            <Link to={`/vendors/${v.id}`} key={v.id} data-testid={`vendor-${v.id}`}
              className={`block rounded-xl border p-4 hover:shadow-md hover:border-emerald-300 transition-all ${v.plan === "featured" ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{v.name}</div>
                  <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 inline-flex items-center gap-1 mt-0.5"><Wrench className="w-2.5 h-2.5"/> {v.category.replace('-',' ')}</div>
                </div>
                {v.plan === "featured" && <div className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 bg-amber-400 text-amber-900 rounded">Featured</div>}
              </div>
              {v.description && <p className="text-xs text-slate-600 mt-2 line-clamp-3">{v.description}</p>}
              <div className="text-[11px] text-slate-500 mt-2 flex flex-wrap gap-2">
                {(v.provinces || []).map(p => <span key={p} className="px-1.5 py-0.5 bg-slate-100 rounded font-bold">{p}</span>)}
              </div>
              <div className="text-xs text-slate-700 mt-3 flex flex-wrap gap-3">
                {v.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/> {v.phone}</span>}
                {v.website && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3"/> Website</span>}
                {v.rate && <span className="text-slate-500">{v.rate}</span>}
              </div>
              <div className="mt-3 text-xs font-semibold text-emerald-700 inline-flex items-center gap-1">
                View profile →
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── PROFILE ──────────────────────────
function ProfilePanel({ userId, scope = "landlord", onTabChange }) {
  const nav = useNavigate();
  const { user: authUser } = useAuth() || {};
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // Auto-save state: 'idle' | 'saving' | 'saved'
  const [autosaveState, setAutosaveState] = useState("idle");
  const autosaveTimerRef = useRef(null);
  const isTenant = scope === "tenant";

  useEffect(() => {
    fetch(`${API}/api/community/profile/${userId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } })
      .then(r => r.json()).then((d) => {
        // Pre-populate the profile form with the signed-in user's account
        // fields when the community profile hasn't been saved yet — so
        // Full name isn't blank on first visit. The user's own POST
        // overwrites this. "Member" is the backend's non-user fallback
        // and should never surface as a real display name.
        const merged = { ...(d || { user_id: userId }) };
        if (!merged.display_name || merged.display_name === "Member") {
          merged.display_name = authUser?.name || authUser?.first_name || "";
        }
        setProfile(merged);
      }).catch(() => setProfile({ user_id: userId, display_name: authUser?.name || "" }));
  }, [userId, authUser]);

  // Auto-save a single field. We POST only the changed field + role +
  // user_id so we never blow away other fields the user is mid-editing.
  const autoSaveField = useCallback(async (field, value) => {
    if (!profile) return;
    setAutosaveState("saving");
    try {
      const body = { [field]: value || null, role: scope, user_id: userId };
      const r = await fetch(`${API}/api/community/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const d = await r.json();
        setProfile(prev => ({ ...prev, ...d }));
        setAutosaveState("saved");
        // Drop back to "idle" 2s after the user stops typing.
        setTimeout(() => setAutosaveState((s) => (s === "saved" ? "idle" : s)), 2000);
      } else {
        setAutosaveState("idle");
      }
    } catch (_) {
      setAutosaveState("idle");
    }
  }, [profile, scope, userId]);

  const debouncedAutoSave = useCallback((field, value) => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => autoSaveField(field, value), 900);
  }, [autoSaveField]);

  const onExit = useCallback(() => {
    if (onTabChange) onTabChange("feed");
    else nav("/community");
  }, [onTabChange, nav]);

  async function save(e) {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const fd = new FormData(e.target);
      const body = {
        display_name: fd.get("display_name") || null,
        bio: fd.get("bio") || null,
        province: fd.get("province") || null,
        role: scope,
        avatar_url: profile.avatar_url || null,
      };
      if (isTenant) {
        body.about_me = fd.get("about_me") || null;
        body.occupation = fd.get("occupation") || null;
        body.hobbies = fd.get("hobbies") || null;
        body.pets = fd.get("pets") || null;
        body.smoker = fd.get("smoker") === "yes";
        body.move_in_ready = fd.get("move_in_ready") === "yes";
      } else {
        body.door_count = fd.get("door_count") ? parseInt(fd.get("door_count")) : null;
      }
      const r = await fetch(`${API}/api/community/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "save failed");
      setProfile(prev => ({ ...prev, ...d }));
      setMsg("Saved.");
      toast.success("Profile saved");
      // Auto-exit the edit section shortly after save so users are not
      // stranded on the form — they land back on the community feed.
      setTimeout(() => { try { onExit(); } catch (_) { /* ignore */ } }, 700);
      } catch (e) { setMsg(String(e.message || e)); toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  }

  if (!profile) return <Loader2 className="w-6 h-6 animate-spin text-emerald-600"/>;
  return (
    <div className="max-w-2xl space-y-4">
      <TopContributorCard userId={userId}/>
      {!isTenant && <MyListingsBoostCard userId={userId}/>}
      <form onSubmit={save} className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="community-profile-form">
      <AvatarUploader userId={userId} profile={profile} onChange={(p) => setProfile(prev => ({ ...prev, avatar_url: p.avatar_url }))}/>
      <div className="flex items-center gap-3">
        {profile.avatar_url ? (
          <img src={profile.avatar_url.startsWith("http") ? profile.avatar_url : `${API}${profile.avatar_url}`} alt={profile.display_name || "You"} className="w-16 h-16 rounded-full object-cover bg-slate-100 border border-slate-200"/>
        ) : (
          <UserCircle2 className="w-16 h-16 text-slate-300 shrink-0"/>
        )}
        <div>
          <div className="font-display text-lg font-extrabold text-slate-900">{profile.display_name || (isTenant ? "Set up your tenant bio" : "Set up your profile")}</div>
          <div className="text-xs text-slate-500 flex items-center gap-3">
            <span>{profile.post_count || 0} posts</span>
            {(profile.points || 0) > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-700 font-bold"><Award className="w-3 h-3"/> {profile.points} pts</span>}
          </div>
        </div>
      </div>
      {isTenant && (
        <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
          Your bio is visible to <b>prospective landlords</b> when they review your rental applications, and to <b>other tenants</b> in the community. Be friendly + honest.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Full name <span className="text-rose-600">*</span></span>
          <input name="display_name" defaultValue={profile.display_name || ""} maxLength={80} required className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500" data-testid="profile-display-name"/>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Province</span>
          <select name="province" defaultValue={profile.province || ""} data-testid="profile-province" className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded">
            <option value="">—</option>
            {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        {!isTenant && (
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Doors managed</span>
            <input type="number" name="door_count" min="0" max="100000" defaultValue={profile.door_count ?? ""} data-testid="profile-door-count" className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500"/>
          </label>
        )}
        {isTenant && (
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Occupation</span>
            <input name="occupation" defaultValue={profile.occupation || ""} placeholder="Software engineer, nurse…" maxLength={120} data-testid="profile-occupation" className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500"/>
          </label>
        )}
      </div>
      {isTenant && (
        <>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">About me</span>
            <textarea name="about_me" defaultValue={profile.about_me || ""} maxLength={1500} rows={4} placeholder="A friendly paragraph about who you are, work, lifestyle..." className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500" data-testid="profile-about-me"/>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Hobbies</span>
              <input name="hobbies" defaultValue={profile.hobbies || ""} placeholder="Hiking, cooking, board games…" maxLength={300} className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500" data-testid="profile-hobbies"/>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Pets</span>
              <input name="pets" defaultValue={profile.pets || ""} placeholder="1 cat (Whiskers), house-trained" maxLength={200} className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500" data-testid="profile-pets"/>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Smoker?</span>
              <select name="smoker" defaultValue={profile.smoker ? "yes" : "no"} data-testid="profile-smoker" className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded">
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Move-in ready</span>
              <select name="move_in_ready" defaultValue={profile.move_in_ready ? "yes" : "no"} data-testid="profile-move-in-ready" className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded">
                <option value="no">Looking 1-3 months out</option>
                <option value="yes">Yes — ready now</option>
              </select>
            </label>
          </div>
        </>
      )}
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Short bio</span>
        <textarea
          name="bio"
          defaultValue={profile.bio || ""}
          maxLength={400}
          rows={3}
          className="mt-1 w-full px-3 py-1.5 text-sm border border-slate-300 rounded outline-none focus:border-emerald-500"
          data-testid="profile-bio"
          onBlur={(e) => { if ((e.target.value || "") !== (profile.bio || "")) autoSaveField("bio", e.target.value); }}
          onChange={(e) => debouncedAutoSave("bio", e.target.value)}
        />
        <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1" data-testid="profile-autosave-status">
          {autosaveState === "saving" && <><Loader2 className="w-2.5 h-2.5 animate-spin" /> Auto-saving…</>}
          {autosaveState === "saved" && <><CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" /> Auto-saved · changes are kept</>}
          {autosaveState === "idle" && <span>Type freely — your bio auto-saves as you go.</span>}
        </div>
      </label>
      {msg && <div className="text-xs text-slate-600">{msg}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={busy} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap" data-testid="profile-save">
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onExit} data-testid="profile-exit"
          className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 whitespace-nowrap">
          Done
        </button>
        <button type="button" onClick={() => nav("/dashboard")} data-testid="profile-exit-dashboard"
          className="ml-auto px-2 py-1 text-[11px] text-slate-500 hover:text-slate-900 hover:underline whitespace-nowrap">
          Exit to dashboard
        </button>
      </div>
    </form>
    </div>
  );
}

// ─────────────────────────── MY LISTINGS + BOOST (landlord) ──────────────────────────
function MyListingsBoostCard({ userId }) {
  const [listings, setListings] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [busy, setBusy] = useState(null);

  async function load() {
    const [r, b] = await Promise.all([
      fetch(`${API}/api/listings?limit=50`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } }),
      fetch(`${API}/api/monetization/listing-boost/active`),
    ]);
    const rd = await r.json(); const bd = await b.json();
    setListings((rd.items || []).filter(l => l.landlord_id === userId));
    setBoosts(bd.items || []);
  }
  useEffect(() => { load(); }, [userId]);

  // Handle the Stripe boost redirect.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("paid") !== "boost") return;
    const sid = sp.get("session_id");
    if (!sid) return;
    fetch(`${API}/api/monetization/listing-boost/status/${sid}`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } })
      .then(() => load())
      .catch(() => {});
  }, [userId]);

  async function boost(lid) {
    setBusy(lid);
    try {
      const r = await fetch(`${API}/api/monetization/listing-boost/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ listing_id: lid, origin: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not start checkout");
      window.location.href = d.url;
    } catch (e) {
      alert(String(e.message || e));
    } finally { setBusy(null); }
  }

  if (listings.length === 0) return null;

  const boostMap = Object.fromEntries(boosts.map(b => [b.listing_id, b]));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="my-listings-boost-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-base font-extrabold text-slate-900 inline-flex items-center gap-2"><Rocket className="w-4 h-4 text-emerald-700"/> Boost your listings</div>
          <p className="text-xs text-slate-600 mt-0.5">Pin a listing to the top of the tenant feed for 7 days. $5 CAD per boost.</p>
        </div>
      </div>
      <div className="space-y-2">
        {listings.map(l => {
          const b = boostMap[l.id];
          const active = !!b;
          return (
            <div key={l.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg p-2.5" data-testid={`my-listing-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm text-slate-900 truncate">{l.title}</div>
                <div className="text-[11px] text-slate-500">{l.city}, {l.province} · ${l.rent_cad}/mo</div>
              </div>
              {active ? (
                <div className="text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-800 inline-flex items-center gap-1" data-testid={`boost-active-${l.id}`}>
                  <Star className="w-2.5 h-2.5 fill-current"/> Boosted · {new Date(b.expires_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                </div>
              ) : (
                <button onClick={() => boost(l.id)} disabled={busy === l.id} className="text-xs font-bold px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid={`boost-${l.id}`}>
                  {busy === l.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <Rocket className="w-3 h-3"/>} Boost — $5
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────── TOP CONTRIBUTOR BADGE CARD ──────────────────────────
function TopContributorCard({ userId }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await fetch(`${API}/api/monetization/badge/mine`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
      const d = await r.json();
      if (r.ok) setStatus(d);
    } catch { /* swallow */ }
  }
  useEffect(() => { load(); }, [userId]);

  async function buy() {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/monetization/badge/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not start checkout");
      window.location.href = d.url;
    } catch (e) {
      alert(String(e.message || e));
    } finally { setBusy(false); }
  }

  if (status?.has_active_badge) {
    const expires = status.items?.[0]?.expires_at;
    return (
      <div className="rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100 p-4" data-testid="top-contributor-active">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-sm">
            <Star className="w-6 h-6 text-amber-950 fill-current"/>
          </div>
          <div>
            <div className="font-display text-base font-extrabold text-amber-950">Top Contributor — Active</div>
            <div className="text-xs text-amber-900">Expires {expires ? new Date(expires).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : ""}.</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="top-contributor-cta">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center shrink-0">
          <Star className="w-6 h-6 text-amber-700"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-extrabold text-amber-950">Get the Top Contributor badge</div>
          <p className="text-xs text-amber-900 mt-0.5">Highlights you across the community for 90 days. Gold star on every post + comment. <b>$9 CAD</b>.</p>
          <button onClick={buy} disabled={busy} data-testid="buy-top-contributor" className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-bold hover:bg-amber-800 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Star className="w-3.5 h-3.5"/>} Get badge — $9
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────── AVATAR UPLOADER ──────────────────────────
function AvatarUploader({ userId, profile, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function pickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (8 MB max)"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("kind", "avatar");
      const r = await fetch(`${API}/api/uploads/image`, { method: "POST", body: form });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      // Persist on the profile immediately.
      const save = await fetch(`${API}/api/community/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ avatar_url: d.url, role: profile.role || "landlord" }),
      });
      if (!save.ok) throw new Error("Could not save photo to profile");
      onChange({ avatar_url: d.url });
      toast.success("Photo updated");
    } catch (err) { toast.error(String(err.message || err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  async function removePhoto() {
    if (!profile.avatar_url) return;
    if (!window.confirm("Remove your profile photo?")) return;
    setBusy(true);
    try {
      // Best-effort delete from object storage. We don't block on it.
      const path = (profile.avatar_url || "").replace(`${API}/api/files/`, "").replace("/api/files/", "");
      if (path) await fetch(`${API}/api/uploads/image?path=${encodeURIComponent(path)}`, { method: "DELETE" }).catch(() => {});
      const r = await fetch(`${API}/api/community/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({ avatar_url: null, role: profile.role || "landlord" }),
      });
      if (!r.ok) throw new Error("Could not remove photo");
      onChange({ avatar_url: null });
      toast.success("Photo removed");
    } catch (err) { toast.error(String(err.message || err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
      <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="profile-photo-input"/>
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="profile-photo-upload" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Camera className="w-3.5 h-3.5"/>} {profile.avatar_url ? "Change photo" : "Add a photo"}
      </button>
      {profile.avatar_url && (
        <button type="button" onClick={removePhoto} disabled={busy} data-testid="profile-photo-delete" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-600 text-xs font-bold hover:bg-rose-50 disabled:opacity-50">
          <Trash2 className="w-3.5 h-3.5"/> Remove photo
        </button>
      )}
      <span className="text-[10px] text-slate-400">JPG, PNG, WEBP, HEIC · max 8 MB</span>
    </div>
  );
}
